import { useState, useEffect } from 'react'
import { Search, Plus, Package, AlertTriangle } from 'lucide-react'
import useInvoiceStore from '../../stores/useInvoiceStore'
import useProductStore from '../../stores/useProductStore'

/**
 * Product Selector Component
 * Search and add products to invoice FROM VEHICLE INVENTORY ONLY
 *
 * Important: Only shows products available in representative's vehicle
 */
const ProductSelector = () => {
  const { addLineItem } = useInvoiceStore()
  const { vehicleInventory, fetchMyVehicleInventory } = useProductStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [showResults, setShowResults] = useState(false)
  const [filteredProducts, setFilteredProducts] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  // Load vehicle inventory on mount
  useEffect(() => {
    const loadInventory = async () => {
      setIsLoading(true)
      try {
        await fetchMyVehicleInventory()
      } catch (error) {
        console.error('Error loading vehicle inventory:', error)
      } finally {
        setIsLoading(false)
      }
    }
    loadInventory()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Filter vehicle inventory based on search query
  // NEW: Show all products when dropdown opens (no search query)
  useEffect(() => {
    // Show all products if no search query
    if (searchQuery.trim().length === 0) {
      setFilteredProducts(vehicleInventory)
      return
    }

    const query = searchQuery.toLowerCase()
    const filtered = vehicleInventory.filter(item => {
      const productName = item.product_name || item.name || ''
      const productCode = item.product_code || item.code || ''

      return productName.toLowerCase().includes(query) ||
             productCode.toLowerCase().includes(query)
    })

    setFilteredProducts(filtered)
  }, [searchQuery, vehicleInventory])

  const handleAddProduct = (product) => {
    addLineItem(product)
    setSearchQuery('')
    setShowResults(false)
  }

  return (
    <div className="space-y-3 relative">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
        إضافة منتجات *
      </h3>

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="ابحث عن منتج لإضافته..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            setShowResults(true)
          }}
          onFocus={() => setShowResults(true)}
          onBlur={() => {
            // Delay hiding to allow click on results
            setTimeout(() => setShowResults(false), 200)
          }}
          className="input pr-10"
        />

        {/* Results Dropdown - Shows all products on focus, filters on search */}
        {showResults && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-gray-600 dark:text-gray-400">
              <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p>جاري التحميل...</p>
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredProducts.map((item) => {
                const isOutOfStock = item.quantity <= 0
                const isLowStock = item.stock_status === 'low'

                return (
                  <button
                    key={item.id}
                    onClick={() => handleAddProduct(item)}
                    disabled={isOutOfStock}
                    className={`w-full text-right p-3 transition-colors ${
                      isOutOfStock
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isOutOfStock
                            ? 'bg-red-100 dark:bg-red-900/30'
                            : isLowStock
                            ? 'bg-yellow-100 dark:bg-yellow-900/30'
                            : 'bg-blue-100 dark:bg-blue-900/30'
                        }`}>
                          <Package className={`w-5 h-5 ${
                            isOutOfStock
                              ? 'text-red-600 dark:text-red-400'
                              : isLowStock
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-blue-600 dark:text-blue-400'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {item.product_name}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <span>{item.category || 'غير مصنف'}</span>
                            <span>•</span>
                            <span className={`font-semibold ${
                              isOutOfStock
                                ? 'text-red-600 dark:text-red-400'
                                : isLowStock
                                ? 'text-yellow-600 dark:text-yellow-400'
                                : 'text-green-600 dark:text-green-400'
                            }`}>
                              متوفر في السيارة: {item.quantity} {item.unit || 'وحدة'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-primary-600 dark:text-primary-400">
                          {(item.unit_price || 0).toLocaleString('ar-KW', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} د.ك
                        </p>
                        {isOutOfStock && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">نفذت الكمية</p>
                        )}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          ) : (
            <div className="p-4 text-center text-gray-600 dark:text-gray-400">
              <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>لا توجد منتجات متاحة في السيارة</p>
              <p className="text-xs mt-1">
                {searchQuery ? 'لا توجد نتائج للبحث' : 'مخزون السيارة فارغ'}
              </p>
            </div>
          )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductSelector
